import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

import { Form as RsuiteForm } from "rsuite";

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

const CompatForm = forwardRef((props, ref) => {
    const {
        formDefaultValue = {},
        formValue,
        onChange,
        ...rest
    } = props;

    const formRef = useRef(null);
    const isControlled = hasOwn(props, "formValue");
    const [internalFormValue, setInternalFormValue] = useState(() => formValue || formDefaultValue || {});
    const formValueRef = useRef(isControlled ? formValue || {} : internalFormValue || {});

    useEffect(() => {
        if (isControlled) {
            formValueRef.current = formValue || {};
        }
    }, [formValue, isControlled]);

    const handleChange = (nextFormValue, event) => {
        formValueRef.current = nextFormValue || {};

        if (!isControlled) {
            setInternalFormValue(nextFormValue || {});
        }

        if (onChange) {
            onChange(nextFormValue, event);
        }
    };

    useImperativeHandle(ref, () => {
        const getForm = () => formRef.current || {};

        const checkForField = (fieldName, callback) => {
            const form = getForm();

            if (props.model?.checkForField) {
                const checkResult = props.model.checkForField(
                    fieldName,
                    formValueRef.current,
                    { nestedObject: props.nestedField }
                );

                if (callback) {
                    callback(checkResult);
                }

                return !checkResult.hasError;
            }

            if (!form.checkForField) {
                return undefined;
            }

            return form.checkForField(fieldName, callback);
        };

        return {
            get root() {
                return getForm().root || null;
            },
            check: (...args) => getForm().check && getForm().check(...args),
            checkAsync: (...args) => getForm().checkAsync && getForm().checkAsync(...args),
            checkForField,
            cleanErrors: (...args) => getForm().cleanErrors && getForm().cleanErrors(...args),
            cleanErrorForField: (...args) => getForm().cleanErrorForField && getForm().cleanErrorForField(...args),
            cleanErrorForFiled: (...args) => {
                const form = getForm();

                if (form.cleanErrorForFiled) {
                    return form.cleanErrorForFiled(...args);
                }

                return form.cleanErrorForField && form.cleanErrorForField(...args);
            },
            resetErrors: (...args) => getForm().resetErrors && getForm().resetErrors(...args),
            reset: (...args) => getForm().reset && getForm().reset(...args),
            submit: (...args) => getForm().submit && getForm().submit(...args),
            getFormValue: () => formValueRef.current || {},
        };
    });

    return (
        <RsuiteForm
            {...rest}
            ref={formRef}
            formDefaultValue={formDefaultValue}
            formValue={isControlled ? formValue : internalFormValue}
            onChange={handleChange}
        />
    );
});

[
    "Control",
    "ControlLabel",
    "ErrorMessage",
    "Group",
    "HelpText",
].forEach(key => {
    CompatForm[key] = RsuiteForm[key];
});

CompatForm.displayName = "CompatForm";

export default CompatForm;
